/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { RtaIconButton } from '../../../components/ui';
import { Badge, Box, Flex, Input, InputGroup, InputLeftElement, Spacer, Stack, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { CurrencyOptions, formatPrice } from '@helpers';
import { PriceTag } from './price-tag';
import { setPricePromotionalSku, setPromotionalDescription, setRemovedAccessory, setRemovedPromotionalSku, setSkuPromotionalSku, useAppDispatch } from '@state';
import { useChangeDescriptionPromotionalSkuFeature, useChangePricePromotionalSkuFeature, useChangeSkuPromotionalSkuFeature, useRemoveAccessoryFeature, useRemovePromotionalSkuFeature } from '@features';

type DiscountBreakdownItem = {
	label: string;
	amount: number;
};

type EstimateItemProps = {
	sku: string
	description: string
	quantity: number
	price: number
	currency: CurrencyOptions
	isMutable?: boolean
	/** Price box only. A multi-rule promotion cannot honour one typed number, so it is read-only there. */
	isPriceMutable?: boolean
	type?: string
	totalAdjustment?: number
	itemType?: string
	discountBreakdown?: Array<DiscountBreakdownItem>
	warning?: string
	discontinued?: boolean
	/** Makes the price box editable for a non-promotional row and receives the typed LINE total (GitHub #513) */
	onPriceChange?: (price: number) => void
//   imageUrl: string
}

export const EstimateItem = (props: EstimateItemProps) => {
	const {
		sku,
		description,
		quantity,
		// imageUrl,
		currency = { locale: 'en-US', currency: 'USD' },
		price,
		isMutable,
		isPriceMutable = true,
		type,
		totalAdjustment,
		itemType = null,
		discountBreakdown,
		warning,
		discontinued,
		onPriceChange
		// onChangeQuantity,
		// onClickDelete,
	} = props;
	const dispatch = useAppDispatch();
	const descriptionColor = useColorModeValue('gray.500', 'gray.300');

	const [deleteSku, setDeleteSku] = useState<string>('');
	const [priceSku, setPriceSku] = useState<string>('');
	const [skuToChange, setSkuToChange] = useState<string>('');
	const [descriptionToChange, setDescriptionToChange] = useState<string>('');

	const removeAccessory = useRemoveAccessoryFeature();

	const removePromotionalSku = useRemovePromotionalSkuFeature();
	const changePricePromotionalSku = useChangePricePromotionalSkuFeature();
	const changeSkuPromotionalSku = useChangeSkuPromotionalSkuFeature();
	const changeDescriptionPromotionalSku = useChangeDescriptionPromotionalSkuFeature();

	const [showInput, setShowInput] = useState<boolean>(false);
	const [inputPrice, setInputPrice] = useState<number | string>(price);

	const [showSkuInput, setShowSkuInput] = useState<boolean>(false);
	const [inputSku, setInputSku] = useState<string>(sku);

	const [showDescriptionInput, setShowDescriptionInput] = useState<boolean>(false);
	const [inputDescription, setInputDescription] = useState<string>(description);

	// Price change effect
	useEffect(() => {
		changePricePromotionalSku.handler();
		setPriceSku('');
	}, [priceSku]);

	// Delete SKU effect
	useEffect(() => {
		if (itemType === 'promotion') removePromotionalSku.handler();
		if (itemType === 'accessory') removeAccessory.handler();
	}, [deleteSku]);

	// SKU change effect - match price change pattern exactly
	useEffect(() => {
		changeSkuPromotionalSku.handler();
		setSkuToChange('');
	}, [skuToChange]);

	// Description change effect - match price change pattern exactly
	useEffect(() => {
		changeDescriptionPromotionalSku.handler();
		setDescriptionToChange('');
	}, [descriptionToChange]);

	// Update input states when props change
	useEffect(() => {
		setInputSku(sku);
	}, [sku]);

	useEffect(() => {
		setInputDescription(description);
	}, [description]);

	useEffect(() => {
		setInputPrice(price);
	}, [price]);

	return (
		<Stack overflow="hidden" roundedLeft={'6px'} direction="row" bg="gray.10" spacing="0" mb="5px" pr="5px" border="1px" borderColor={'gray.400'}>
			<Box pos="relative" bg="gray.200" pl="5px" pr="5px" pt="0">
				{/* <Image
					rounded="md"
					minWidth="10"
					maxWidth="10"
					height={{ base: '10', md: '10' }}
					fit="cover"
					// src={imageUrl}
					// alt={name}
					draggable="false"
					loading="lazy"
				/> */}
				<Box alignContent={'center'} textAlign="center" width="1em" height="100%" margin="auto">
					<Text fontSize={'lg'}>{quantity}</Text>
				</Box>
			</Box>
			<VStack spacing="0.1" width="full" align={'left'}>
				{ !showSkuInput &&
				<Text
					ml="5px"
					borderBottom="1px"
					borderColor="gray.300"
					fontSize="md"
					fontWeight="medium"
					cursor={isMutable ? 'pointer' : 'default'}
					onClick={() => {
						if (isMutable) setShowSkuInput(true);
					}}
				>
					{inputSku}
					{discontinued && <Badge ml="5px" colorScheme="red">Discontinued</Badge>}
				</Text>
				}
				{ showSkuInput &&
				<InputGroup pl="5px" pb="5px">
					<InputLeftElement pointerEvents="none">
						<Text fontSize="sm">SKU:</Text>
					</InputLeftElement>
					<Input
						type="text"
						value={inputSku}
						onChange={(e) => setInputSku(e.target.value)}
						onBlur={() => {
							setShowSkuInput(false);
							// Exactly match price pattern: dispatch action and set trigger state
							dispatch(setSkuPromotionalSku([sku, inputSku]));
							setSkuToChange(sku);
						}}
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								setShowSkuInput(false);
								dispatch(setSkuPromotionalSku([sku, inputSku]));
								setSkuToChange(sku);
							}
						}}
						autoFocus
					/>
				</InputGroup>
				}
				<Flex pl="5px" justifyContent="space-between">
					{ !showDescriptionInput &&
					<Text
						color={discontinued ? 'red.600' : descriptionColor}
						fontSize="sm"
						cursor={isMutable ? 'pointer' : 'default'}
						onClick={() => {
							if (isMutable) setShowDescriptionInput(true);
						}}
					>
						{inputDescription}
					</Text>
					}
					{ showDescriptionInput &&
						<InputGroup pl="5px" pb="5px">
							<InputLeftElement pointerEvents="none">
								<Text fontSize="sm">Description:</Text>
							</InputLeftElement>
							<Input
								type="text"
								value={inputDescription}
								onChange={(e) => setInputDescription(e.target.value)}
								onBlur={() => {
									setShowDescriptionInput(false);
									// Exactly match price pattern: dispatch action and set trigger state
									dispatch(setPromotionalDescription([sku, inputDescription]));
									setDescriptionToChange(sku);
								}}
								onKeyPress={(e) => {
									if (e.key === 'Enter') {
										setShowDescriptionInput(false);
										dispatch(setPromotionalDescription([sku, inputDescription]));
										setDescriptionToChange(sku);
									}
								}}
								autoFocus
							/>
						</InputGroup>
					}
				</Flex>
				<Flex justifyContent="space-between">
					<Spacer/>
					{ !showInput &&
							<PriceTag priceProps={{ fontSize: 'sm' }} price={price} currency={currency} totalAdjustment={totalAdjustment} type={type} onClick={() => {
								if ((isMutable || onPriceChange) && isPriceMutable) setShowInput(true);
							}}/>
					}

					{showInput &&
					<InputGroup pl="5px" pb="5px">
						<InputLeftElement
							pointerEvents="none"
							color="gray.300"
							mt="0"
						>$</InputLeftElement>
						<Input height="fit-content" fontSize="md" type="number" value={inputPrice === 0 ? '' : inputPrice} onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setInputPrice(isNaN(Number(event.target.value)) ? '' : Number(event.target.value));
						}}

						onBlur={() => {
							if (onPriceChange) {
								onPriceChange(Number(inputPrice) || 0);
							} else {
								dispatch(setPricePromotionalSku([sku, inputPrice]));
								setPriceSku(sku);
							}
							setShowInput(false);
						}}
						style={{
							// width: '50px',
							// height: '30px',
							// padding: '5px',
							// margin: '10px 0px',
							// border: '2px solid',
							borderRadius: '5px'
						}}/>
					</InputGroup>
					}

					{ isMutable &&
						<RtaIconButton enabled={ !showInput } tabIndex={-1} height={'1.25em'} color={'red.500'} icon={removePromotionalSku.icon()} onClick={() => {
							if (itemType === 'promotion') dispatch(setRemovedPromotionalSku(sku));
							if (itemType === 'accessory') dispatch(setRemovedAccessory(sku));
							setDeleteSku(sku);
						} }/>
					}
				</Flex>
				{discountBreakdown && discountBreakdown.length > 0 && (
					<Box pl="5px" pr="5px" pb="3px" borderTop="1px" borderColor="gray.200">
						{discountBreakdown.map((breakdownItem) => {
							const { label, amount } = breakdownItem;
							return (
								<Flex key={label} justifyContent="space-between" fontSize="xs">
									<Text>{label}</Text>
									<Text>{formatPrice(amount, currency)}</Text>
								</Flex>
							);
						})}
					</Box>
				)}
				{warning && (
					<Box pl="5px" pr="5px" pb="3px">
						<Text fontSize="xs" color="orange.500" fontWeight="medium">{warning}</Text>
					</Box>
				)}
			</VStack>
		</Stack>
	);
};
